import { IconToggleButton, LuIcon } from "@overmap-ai/blocks"
import { type ComponentPropsWithRef, memo, useCallback } from "react"

import { HeadingLevel } from "../../enums"
import { useEditorFocused, useFallbackEditor, useHeadingState } from "../../hooks"
import { headingDataMapping } from "../mappings"
import type { OptionalEditorProps } from "../typings"

interface HeadingToggleButtonProps
	extends Omit<ComponentPropsWithRef<typeof IconToggleButton>, "children">, OptionalEditorProps {
	level: HeadingLevel
}

export const HeadingToggleButton = memo<HeadingToggleButtonProps>((props) => {
	const { ref, level, editor: editorFromProps, pressed, onPressedChange, disabled, ...rest } = props

	const editor = useFallbackEditor(editorFromProps)
	const isEditorFocused = useEditorFocused(editor)
	const state = useHeadingState(editor)

	const handlePressedChange = useCallback(
		(pressed: boolean) => {
			onPressedChange?.(pressed)
			editor?.chain().focus().toggleHeading({ level }).run()
		},
		[onPressedChange, editor, level],
	)

	return (
		<IconToggleButton
			ref={ref}
			pressed={pressed ?? (state.isActive(level) && isEditorFocused)}
			onPressedChange={handlePressedChange}
			disabled={disabled ?? !state.canToggle(level)}
			{...rest}
		>
			<LuIcon icon={headingDataMapping[level].icon} />
		</IconToggleButton>
	)
})
HeadingToggleButton.displayName = "HeadingToggleButton"
